import DynamicFormFields from '@components/common/dynamicFormFields/DynamicFormFields';
import Modal from '@components/common/modal/Modal';
import {
  HcLinearTable2NonPersisted,
  SortDirectionDTO,
  SortOrderDTO,
} from '@hclinear/hc-linear-react';
import { Box, Grid } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { format, subDays } from 'date-fns';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '@components/common/button/Button';
import {
  DATE_TIME_FORMAT_ISO,
  TABLE_DEFAULT_PAGE_SIZE,
  TABLE_PAGE_SIZE_OPTIONS,
} from '@/constant';
import { useSearchTerminalStatusQuery } from '@/hooks';
import { TerminalStatusFilterDTO } from '@/model';
import {
  terminalStatusHistoryFilterFormFields,
  terminalStatusHistoryTableColumns,
  TerminalStatusHistoryRow,
} from '@/page/stopTerminalStatus/data/stopTerminalStatus.data';

interface TerminalStatusHistoryModalProps {
  open: boolean;
  handleClose: () => void;
  productSnId: number;
  productSnName: string;
}

type DateRangeFilter = Pick<
  TerminalStatusFilterDTO,
  'insertDateGreaterThen' | 'insertDateLessThen'
>;

const getInitialDateRangeFilter = (): DateRangeFilter => ({
  insertDateGreaterThen: format(subDays(new Date(), 1), DATE_TIME_FORMAT_ISO),
  insertDateLessThen: undefined,
});

const DEFAULT_ORDERS: SortOrderDTO[] = [
  { fieldName: 'insertDate', direction: SortDirectionDTO.DESC },
];

const mapOrdersToSortModel = (orders: SortOrderDTO[]): GridSortModel =>
  orders.map((order) => ({
    field: order.fieldName,
    sort: order.direction === SortDirectionDTO.ASC ? 'asc' : 'desc',
  }));

const mapSortModelToOrders = (sortModel: GridSortModel): SortOrderDTO[] =>
  sortModel
    .filter((item) => !!item.sort)
    .map((item) => ({
      fieldName: item.field,
      direction: item.sort === 'asc' ? SortDirectionDTO.ASC : SortDirectionDTO.DESC,
    }));

const TerminalStatusHistoryModal = ({
  open,
  handleClose,
  productSnId,
  productSnName,
}: TerminalStatusHistoryModalProps) => {
  const { t } = useTranslation();
  const methods = useForm<DateRangeFilter>({
    defaultValues: getInitialDateRangeFilter(),
  });

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(TABLE_DEFAULT_PAGE_SIZE);
  const [orders, setOrders] = useState<SortOrderDTO[]>(DEFAULT_ORDERS);
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>(
    getInitialDateRangeFilter,
  );

  const { data: page, isFetching } = useSearchTerminalStatusQuery({
    pageNumber,
    pageSize,
    orders,
    filters: { ...dateRangeFilter, productSnId },
  });

  const applyDateRangeFilter = (result: DateRangeFilter) => {
    setDateRangeFilter(result);
    setPageNumber(0);
  };

  const rows: TerminalStatusHistoryRow[] = (page?.content ?? []).map((status, index) => ({
    ...status,
    id: pageNumber * pageSize + index,
  }));

  return (
    <Modal
      title={'stop-terminal-status.terminal-status-history-modal.title'}
      open={open}
      onClose={handleClose}
      maxWidth='95vw'
      dialogContentStyle={{ width: '90vw' }}
    >
      <FormProvider {...methods}>
        <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
          <DynamicFormFields<DateRangeFilter>
            formFields={terminalStatusHistoryFilterFormFields(t)}
            control={methods.control}
          />
          <Grid item>
            <Button
              variant='contained'
              onClick={methods.handleSubmit(applyDateRangeFilter)}
            >
              {t('common.form.filter')}
            </Button>
          </Grid>
        </Grid>
      </FormProvider>
      <Box sx={{ height: '60vh', minHeight: 0 }}>
        <HcLinearTable2NonPersisted<TerminalStatusHistoryRow>
          rowHeight={50}
          loading={isFetching}
          getRowId={(row) => row.id}
          rows={rows}
          columns={terminalStatusHistoryTableColumns(t)}
          containerStyle={{ height: '100%', minHeight: 0 }}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          hideFooterSelectedRowCount
          pagination
          paginationMode='server'
          rowCount={page?.totalElements ?? 0}
          paginationModel={{ page: pageNumber, pageSize }}
          onPaginationModelChange={(model: { page: number; pageSize: number }) => {
            setPageNumber(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode='server'
          sortModel={mapOrdersToSortModel(orders)}
          onSortModelChange={(sortModel: GridSortModel) => {
            setOrders(
              sortModel.length ? mapSortModelToOrders(sortModel) : DEFAULT_ORDERS,
            );
            setPageNumber(0);
          }}
          renderTopToolbarCustomActions={() => (
            <span style={{ fontSize: '1.25rem', fontWeight: 500 }}>{productSnName}</span>
          )}
        />
      </Box>
    </Modal>
  );
};

export default TerminalStatusHistoryModal;
